import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";
import { MaterialSelect } from "@/components/slot/MaterialSelect";
import {
  cestaPolozky,
  useUlozPolozku,
  type ObjednavkyRiadok,
} from "@/api/hooks/useObjednavky";

/**
 * Zámena položky za inú + poznámka.
 *
 * **Obe cesty sú naschvál.** Keď náhrada v cenníku je, vyberie sa (má kód,
 * dodávateľa aj cenu). Keď nie je, napíše sa voľným textom — keby náhrada
 * musela byť cenníková položka, kolega si ju v zhone nikam nezapíše
 * a informácia sa stratí úplne.
 */
export function NahradaDialog({
  riadok,
  zakazkaId,
  onClose,
}: {
  riadok: ObjednavkyRiadok;
  zakazkaId: string;
  onClose: () => void;
}): JSX.Element {
  const uloz = useUlozPolozku(zakazkaId);
  const [materialId, setMaterialId] = useState<string | null>(
    riadok.nahrada_material_id,
  );
  const [text, setText] = useState(riadok.nahrada_text ?? "");
  const [eshop, setEshop] = useState(riadok.eshop ?? "");
  const [cislo, setCislo] = useState(riadok.cislo_objednavky ?? "");
  const [dodanie, setDodanie] = useState(riadok.dodanie_ocakavane ?? "");
  const [poznamka, setPoznamka] = useState(riadok.poznamka ?? "");
  // Vlastná položka mimo cenníka nemá náhradu z cenníka, len textom (KOLO 211)
  const vlastna = riadok.material_id == null;

  async function ulozZmeny(): Promise<void> {
    try {
      await uloz.mutateAsync({
        polozka: cestaPolozky(riadok),
        data: {
          // Stav sa nemení — dialóg je o tom, čo sa objednalo namiesto
          // toho, nie o posune v procese
          stav: riadok.stav === "caka" ? "caka" : riadok.stav,
          nahrada_material_id: vlastna ? undefined : materialId,
          nahrada_text: text,
          eshop,
          cislo_objednavky: cislo,
          dodanie_ocakavane: dodanie || null,
          poznamka,
        },
      });
      onClose();
    } catch (e) {
      toast({
        variant: "destructive",
        title: "Neuložené",
        description: extractErrorMessage(e),
      });
    }
  }

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Náhrada a detaily objednávky</DialogTitle>
          <DialogDescription>
            {riadok.kod} {riadok.nazov}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {vlastna ? null : (
            <div className="space-y-1">
              <Label className="text-xs">Náhrada z cenníka</Label>
              <MaterialSelect
                value={materialId}
                onChange={(id) => setMaterialId(id)}
                placeholder="Vyhľadať náhradu v cenníku…"
                // Náhrada býva práve od iného dodávateľa — výber ju nesmie
                // schovať za preferovaného (KOLO 166, tak to MaterialSelect
                // pri `vsetciDodavatelia` opisuje)
                vsetciDodavatelia
              />
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="nahrada-text" className="text-xs">
              Náhrada, ktorá v cenníku nie je
            </Label>
            <Input
              id="nahrada-text"
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder="napr. rovnaký dekor od iného dodávateľa"
            />
          </div>

          <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
            <div className="space-y-1">
              <Label htmlFor="nahrada-eshop" className="text-xs">
                Eshop
              </Label>
              <Input
                id="nahrada-eshop"
                value={eshop}
                onChange={(e) => setEshop(e.target.value)}
                placeholder="hranipex"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nahrada-cislo" className="text-xs">
                Číslo objednávky
              </Label>
              <Input
                id="nahrada-cislo"
                value={cislo}
                onChange={(e) => setCislo(e.target.value)}
                placeholder="OBJ-2026-114"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="nahrada-dodanie" className="text-xs">
                Dodanie
              </Label>
              <Input
                id="nahrada-dodanie"
                type="date"
                value={dodanie}
                onChange={(e) => setDodanie(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="nahrada-poznamka" className="text-xs">
              Poznámka
            </Label>
            <Input
              id="nahrada-poznamka"
              value={poznamka}
              onChange={(e) => setPoznamka(e.target.value)}
              placeholder="čokoľvek, čo má kolega vedieť"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Zrušiť
          </Button>
          <Button disabled={uloz.isPending} onClick={() => void ulozZmeny()}>
            {uloz.isPending ? "Ukladám…" : "Uložiť"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
